import { ElementRef } from '@angular/core';
import { DateUtility } from '../../classes/dateUtility';
import { COLORS } from '../../interfaces/constants';
import { IHighChartCustomSeries } from '../available-water-chart/interfaces';
import highChartExportOptions from '../shared/highchartsExportOptions';
import { AppliedFertilizerChartDataSet } from './interfaces';
import * as Highcharts from 'highcharts';
require('highcharts/modules/pattern-fill')(Highcharts);

/** diagonal hatch used to mark the N deficient portion of the balance curve */
const N_DEFICIT_HATCH = {
	pattern: {
		path: {
			d: 'M 0 8 L 8 0',
			strokeWidth: 1.5
		},
		color: 'rgba(220, 53, 69, 0.7)',
		width: 8,
		height: 8
	}
};

export class NBalanceChartService {
	private _chartData: Highcharts.IndividualSeriesOptions[];
	private min: number; // min max date number values for displaying the chart
	private max: number;
	private _eventDate: Date;
	private _daysToNextFertilizationEvent: number;

	constructor(
		private _chartContainer: ElementRef
	) {}

	public convertToGraphData(records: AppliedFertilizerChartDataSet, eventDate:  Date,
		daysToNext: number): void {

		let yMin = 0;
		let yMax = 0;

		if (!records) {
			return;
		}

		this._eventDate = eventDate;
		this._daysToNextFertilizationEvent = daysToNext;

		let temp: {
			balance: Highcharts.AreaChartSeriesOptions,
			AppliedDaily: IHighChartCustomSeries,
			EstimatedAppliedDaily: IHighChartCustomSeries,
			NinWater: IHighChartCustomSeries
			eventDate: Highcharts.LineChartSeriesOptions,
			nextFertilization: Highcharts.LineChartSeriesOptions
		};

		temp = {
			balance: {
				name: 'N Balance',
				visible: true,
				type: 'area',
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				},
				// only the portion of the curve below zero is filled, marking an N deficit
				threshold: 0,
				fillColor: 'rgba(0, 0, 0, 0)',
				negativeFillColor: N_DEFICIT_HATCH as any
			},
			AppliedDaily: {
				name: 'N Applied',
				visible: true,
				type: 'column',
				data: [],
				pointWidth: 5,
				stack: 'applied',
				tooltip: {
					valueSuffix: ' lbs N/acre'
				}
			},
			EstimatedAppliedDaily: {
				name: 'Estimated N Applied',
				visible: true,
				type: 'column',
				data: [],
				pointWidth: 5,
				stack: 'applied',
				color: '#F0A202',
				tooltip: {
					valueSuffix: ' lbs N/acre'
				}
			},
			NinWater: {
				name: 'N in Water',
				visible: true,
				type: 'column',
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
			},
			eventDate: {
				name: 'Event Date',
				type: 'line',
				color: '#ADB5BD',
				data: [],
				enableMouseTracking: false,
				showInLegend: true,
				marker: { enabled: false },
				lineWidth: 1,
				dashStyle: 'Solid'
			},
			nextFertilization: {
				name: 'Next Fertilization',
				type: 'line',
				color: 'blue',
				data: [],
				enableMouseTracking: false,
				showInLegend: true,
				marker: { enabled: false },
				lineWidth: 2,
				dashStyle: 'Dash'
			}
		}

		for (let balanceRecord of records.Balance) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(balanceRecord.EventDate));
			temp.balance.data.push([utc, balanceRecord.Amount]);

			yMin = balanceRecord.Amount < yMin ? balanceRecord.Amount : yMin;
			yMax = balanceRecord.Amount > yMax ? balanceRecord.Amount : yMax;
		}

		for (let AppliedDailyRecord of records.AppliedDaily) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(AppliedDailyRecord.EventDate));
			temp.AppliedDaily.data.push([utc, AppliedDailyRecord.Amount]);
		}

		let hasEstimated = false;

		for (let estimatedRecord of records.EstimatedAppliedDaily || []) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(estimatedRecord.EventDate));
			temp.EstimatedAppliedDaily.data.push([utc, estimatedRecord.Amount]);

			hasEstimated = hasEstimated || estimatedRecord.Amount > 0;
		}

		for (let NinWaterRecord of records.NinWater) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(NinWaterRecord.EventDate));
			temp.NinWater.data.push([utc, NinWaterRecord.Amount]);
		}

		if (this._eventDate) {
			let utc: number;

			utc = DateUtility.DateToUTC(this._eventDate);
			temp.eventDate.data.push([utc, yMin]);
			temp.eventDate.data.push([utc, yMax]);
		}

		if (this._daysToNextFertilizationEvent && this._eventDate) {
			let nextDate: Date;
			let utc: number;

			nextDate = DateUtility.addDays(this._eventDate,
				this._daysToNextFertilizationEvent);

			utc = DateUtility.DateToUTC(nextDate);
			temp.nextFertilization.data.push([utc, yMin]);
			temp.nextFertilization.data.push([utc, yMax]);
		}

		const { minDate, maxDate } = records.Balance.reduce((acc, curr) => {
			let date: number;

			date = DateUtility.DateToUTC(DateUtility.DotNetToDate(curr.EventDate));

			return {
				minDate: date < acc.minDate ? date : acc.minDate,
				maxDate: date > acc.maxDate ? date : acc.maxDate
			};
		}, { minDate: Infinity, maxDate: -Infinity });

		this.min = minDate;
		this.max = maxDate;

		this._chartData = [];

		this._chartData.push(temp.balance);
		this._chartData.push(temp.AppliedDaily);

		if (hasEstimated) {
			this._chartData.push(temp.EstimatedAppliedDaily);
		}

		this._chartData.push(temp.NinWater);
		this._chartData.push(temp.eventDate);

		if (this._daysToNextFertilizationEvent) {
			this._chartData.push(temp.nextFertilization);
		}
	}

	public drawChart(): Highcharts.ChartObject {
		let options: Highcharts.Options;

		if (!this._chartContainer) {
			return;
		}

		options = {
			chart: {
			// type: 'line',
				width: 733,
				height: 320,
				spacingBottom: 5,
			},
			title: {
				text: null,
			},
			legend: {
				margin: 6,
				title: {
					text: null,
				}
			},
			tooltip: {
				backgroundColor: COLORS.WHITE,
				borderColor: COLORS.WHITE,
				valueDecimals: 2,
				valueSuffix: 'lbs N/acre',
			},
			xAxis: {
				type: 'datetime',
				gridLineWidth: 1,
				min: this.min,
				max: this.max,
				plotLines: []
			},
			yAxis: [{
				min: -10,
				title: {
					text: 'Lbs N/acre'
				}
			}],
			plotOptions: {
				column: {
					stacking: 'normal'
				}
			},
			series: this._chartData,
			exporting: highChartExportOptions,
			credits: {
				enabled: false
			}
		}

		return Highcharts.chart(this._chartContainer.nativeElement, options);
	}
}
